import React from 'react';
import { defineMessages, type IntlShape } from 'react-intl';
import classNames from 'classnames';

import { GA4EventName } from '@jsmdg/tracking';
import { Dropdown, RenderType, ToggleButton } from '@jsmdg/yoshi';
import { type SelectOption } from '@jsmdg/yoshi/dist/types/selectOption';
import { ProductType } from '../../../shared/enums';
import { type ProductGroupItem as ProductGroupItemType } from '../../../shared/types';
import { formatPrice } from '../../helper/formatPrice';
import { Divider } from '../Divider/Divider';
import { RedeemInfo } from '../RedeemInfo';
import { RedemptionInfoBanner } from '../RedemptionInfoBanner/RedemptionInfoBanner';
import { removeLastLetter, sortActivityDuration } from './activityDuration';
import {
    trackDropdownClicked,
    trackDropDownOpened,
    trackProductItemClicked,
} from './productGroup.tracking';
import { ProductGroupItem } from './ProductGroupItem';
import styles from './ProductGroup.module.scss';

type BuildProductGroupProps = {
    items: ProductGroupItemType[];
    selectedItemId: string;
    setSelectedItemId: React.Dispatch<React.SetStateAction<string>>;
    intl: IntlShape;
    isRedemptionCartFilled: boolean;
    productType?: ProductType;
};

const messages = defineMessages({
    voucherAmount: {
        defaultMessage: 'Gutscheinbetrag',
    },
    experienceDuration: {
        defaultMessage: 'Erlebnisdauer',
    },
    experienceDurationDays: {
        defaultMessage: 'Tage',
    },
    experienceDurationHours: {
        defaultMessage: 'Stunden',
    },
    experienceDurationMinutes: {
        defaultMessage: 'Minuten',
    },
});

const minimumAmountForProductGroupRender = 2;
const limitForValueVoucherPills = 3;
const limitForExperienceDurationPills = 3;

const setSelectedItem = (
    url: string,
    setSelectedItemId: React.Dispatch<React.SetStateAction<string>>,
): void => {
    setSelectedItemId(url.split('/').reverse()[0]);
};

const goToProduct = (
    url: string,
    label: string,
    setSelectedItemId: React.Dispatch<React.SetStateAction<string>>,
    eventName?: GA4EventName,
): void => {
    setSelectedItem(url, setSelectedItemId);
    trackDropdownClicked(label, eventName);
    window.location.assign(url);
};

function formatActivityDurationLabel(
    activityDuration: { days?: number; hours?: number; minutes?: number } | undefined,
    intl: IntlShape,
): string {
    return activityDuration
        ? [
              activityDuration.days
                  ? `${activityDuration.days} ${removeLastLetter(
                        intl.formatMessage(messages.experienceDurationDays),
                        activityDuration.days,
                    )}`
                  : null,
              activityDuration.hours
                  ? `${activityDuration.hours} ${removeLastLetter(
                        intl.formatMessage(messages.experienceDurationHours),
                        activityDuration.hours,
                    )}`
                  : null,
              activityDuration.minutes
                  ? `${activityDuration.minutes} ${removeLastLetter(
                        intl.formatMessage(messages.experienceDurationMinutes),
                        activityDuration.minutes,
                    )}`
                  : null,
          ]
              .filter(Boolean)
              .join(' ')
        : '';
}

function buildBoxAndExperienceProductGroup(
    items: ProductGroupItemType[],
    isRedemptionCartFilled: boolean,
    selectedItemId: string,
    setSelectedItemId: React.Dispatch<React.SetStateAction<string>>,
): JSX.Element {
    return (
        <div
            className={classNames(
                'd-flex pt-2x flex-column px-3x px-xs-6x',
                styles.productGroupWrapper,
            )}
            data-testid="product-group-box-product"
        >
            {items
                .sort(a => (a.type === ProductType.BOX ? -1 : 1))
                .map(({ productId, type, url }) => (
                    <ProductGroupItem
                        key={productId}
                        url={url}
                        type={ProductType[type as ProductType]}
                        isProductGroupItemSelected={productId === selectedItemId}
                        onChange={() => {
                            setSelectedItem(url, setSelectedItemId);
                            window.location.assign(url);
                        }}
                    />
                ))}
            {isRedemptionCartFilled && <RedemptionInfoBanner />}
        </div>
    );
}

function buildValueVoucherGroup({
    intl,
    isRedemptionCartFilled,
    items,
    selectedItemId,
    setSelectedItemId,
}: BuildProductGroupProps): JSX.Element {
    const displayItems = items
        .sort((a, b) => (a.price?.gross.amount as number) - (b.price?.gross.amount as number))
        .map(({ price, productId, url }) => {
            const label = formatPrice(
                intl.locale,
                price?.gross.amount as number,
                price?.gross.currencyCode as string,
            );
            return {
                productId,
                value: url,
                label,
            };
        });

    const selectedItem = displayItems.find(item => item.productId === selectedItemId);

    return (
        <div
            data-testid="product-group-value-voucher-product"
            className={classNames('pt-2x px-3x px-xs-6x', styles.productGroupWrapper)}
        >
            <strong className="mb-1x d-block theme-text-subtitle-text">
                {intl.formatMessage(messages.voucherAmount)}
            </strong>
            <div className="mb-2x">
                <RedeemInfo />
            </div>
            {displayItems.length <= limitForValueVoucherPills && (
                <>
                    {displayItems.map(({ label, productId, value }) => {
                        return (
                            <ToggleButton
                                key={productId}
                                className="mr-1x mb-3x"
                                href={value}
                                onClick={() => {
                                    trackProductItemClicked(label, value);
                                }}
                                as={RenderType.Link}
                                isSelected={selectedItemId === productId}
                                a11yText={label}
                            >
                                {label}
                            </ToggleButton>
                        );
                    })}
                    <Divider />
                </>
            )}
            {displayItems.length > limitForValueVoucherPills && (
                <Dropdown
                    label={intl.formatMessage(messages.voucherAmount)}
                    ariaLabel={intl.formatMessage(messages.voucherAmount)}
                    options={displayItems as unknown as SelectOption[]}
                    value={selectedItem?.value}
                    disabled={isRedemptionCartFilled}
                    onChange={(url: React.Key) => {
                        const currentItem = displayItems.find(item => item.value === url);
                        goToProduct(
                            url as string,
                            currentItem?.label as string,
                            setSelectedItemId,
                            GA4EventName.SelectValueVoucherAmount,
                        );
                    }}
                    dataTestId="select-voucherAmount"
                    className="d-block"
                    onClick={() => {
                        trackDropDownOpened();
                    }}
                />
            )}
            {isRedemptionCartFilled && <RedemptionInfoBanner />}
        </div>
    );
}

function buildExperienceProductGroup({
    intl,
    isRedemptionCartFilled,
    items,
    selectedItemId,
    setSelectedItemId,
}: BuildProductGroupProps): JSX.Element {
    const displayItems = items
        .sort(sortActivityDuration)
        .map(({ activityDuration, productId, url }) => {
            const label = formatActivityDurationLabel(activityDuration, intl);
            return {
                productId,
                value: url,
                label,
            };
        });

    const selectedItem = displayItems.find(item => item.productId === selectedItemId);

    return (
        <div
            data-testid="product-group-experience-product"
            className={classNames('pt-2x px-3x px-xs-6x pb-0-5x', styles.productGroupWrapper)}
        >
            <strong className="mr-1x mb-1x d-block">
                {intl.formatMessage(messages.experienceDuration)}
            </strong>
            {displayItems.length <= limitForExperienceDurationPills && (
                <>
                    {displayItems.map(({ label, productId, value }) => {
                        return (
                            <ToggleButton
                                key={productId}
                                className="mr-1x mb-1x"
                                href={value}
                                onClick={() => {
                                    trackProductItemClicked(label, value);
                                }}
                                as={RenderType.Link}
                                isSelected={selectedItemId === productId}
                                a11yText={label}
                            >
                                {label}
                            </ToggleButton>
                        );
                    })}
                </>
            )}

            {displayItems.length > limitForExperienceDurationPills && (
                <Dropdown
                    label={intl.formatMessage(messages.experienceDuration)}
                    ariaLabel={intl.formatMessage(messages.experienceDuration)}
                    options={displayItems as unknown as SelectOption[]}
                    value={selectedItem?.value}
                    disabled={isRedemptionCartFilled}
                    onChange={(url: React.Key) => {
                        const currentItem = displayItems.find(item => item.value === url);
                        goToProduct(url as string, currentItem?.label as string, setSelectedItemId);
                    }}
                    dataTestId="select-experienceDuration"
                    className="d-block"
                    overlayClasses={styles.box}
                    onClick={() => {
                        trackDropDownOpened();
                    }}
                />
            )}
        </div>
    );
}

function buildProductGroup({
    intl,
    isRedemptionCartFilled,
    items,
    productType,
    selectedItemId,
    setSelectedItemId,
}: BuildProductGroupProps): {
    body: JSX.Element | null;
    canRender: boolean;
} {
    const hasBoxProductInGroup = items.some(i => i.type === ProductType.BOX);
    const hasExperienceProductInGroup = items.some(i => i.type === ProductType.EXPERIENCE);
    const hasOnlyExperienceProductsInGroup =
        items.length > 0 && items.every(i => i.type === ProductType.EXPERIENCE);
    const hasOnlyValueVoucherProductsInGroup =
        items.length > 0 && items.every(i => i.type === ProductType.VALUE_VOUCHER);
    const isValueVoucher = productType === ProductType.VALUE_VOUCHER;

    if (
        items.length === minimumAmountForProductGroupRender &&
        hasBoxProductInGroup &&
        hasExperienceProductInGroup
    ) {
        return {
            body: buildBoxAndExperienceProductGroup(
                items,
                isRedemptionCartFilled,
                selectedItemId,
                setSelectedItemId,
            ),
            canRender: true,
        };
    }

    if (isValueVoucher) {
        if (
            hasOnlyValueVoucherProductsInGroup &&
            items.length >= minimumAmountForProductGroupRender
        ) {
            return {
                body: buildValueVoucherGroup({
                    items,
                    selectedItemId,
                    setSelectedItemId,
                    intl,
                    isRedemptionCartFilled,
                }),
                canRender: true,
            };
        }

        return {
            body: (
                <div
                    data-testid="product-group-value-voucher-product"
                    className={classNames('px-3x px-xs-6x', styles.productGroupWrapper)}
                >
                    <RedeemInfo />
                </div>
            ),
            canRender: true,
        };
    }

    const experienceProductGroupItems = items.filter(i => i.activityDuration);

    if (
        experienceProductGroupItems.length >= minimumAmountForProductGroupRender &&
        hasOnlyExperienceProductsInGroup
    ) {
        return {
            body: buildExperienceProductGroup({
                items: experienceProductGroupItems,
                selectedItemId,
                setSelectedItemId,
                intl,
                isRedemptionCartFilled,
            }),
            canRender: true,
        };
    }

    return {
        body: null,
        canRender: false,
    };
}

export { buildProductGroup };
